import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBox,
  FaSearch,
  FaFilter,
  FaEdit,
  FaTrash,
  FaEye,
  FaRupeeSign,
  FaChartLine,
  FaPlus,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChevronLeft,
  FaChevronRight,
  FaRedo,
  FaDownload,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { GiMedicinePills } from 'react-icons/gi';

// Firebase imports
import { database } from '../firebase'; // Adjust path if needed
import { ref, onValue, remove } from 'firebase/database';

const AdminViewProducts = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'productName', direction: 'ascending' });
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    inStock: 0,
    outOfStock: 0,
    lowStock: 0
  });

  const statusOptions = ['All', 'In Stock', 'Out of Stock', 'Low Stock', 'Coming Soon', 'Discontinued'];

  // Fetch products from Firebase Realtime Database
  useEffect(() => {
    const productsRef = ref(database, 'Dr/products');

    const unsubscribe = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productsList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setProducts(productsList);
        setFilteredProducts(productsList);
        calculateStats(productsList);
      } else {
        setProducts([]);
        setFilteredProducts([]);
        setStats({ total: 0, inStock: 0, outOfStock: 0, lowStock: 0 });
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching products:', error);
      setLoading(false);
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, []);

  // Filter and sort when dependencies change
  useEffect(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(product => product.status === statusFilter);
    }

    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'finalPrice' || sortConfig.key === 'price') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    setFilteredProducts(filtered);
    calculateStats(filtered);
  }, [searchQuery, statusFilter, products, sortConfig]);

  const calculateStats = (productList) => {
    const total = productList.length;
    const inStock = productList.filter(p => p.status === 'In Stock').length;
    const outOfStock = productList.filter(p => p.status === 'Out of Stock').length;
    const lowStock = productList.filter(p => p.status === 'Low Stock').length;

    setStats({ total, inStock, outOfStock, lowStock });
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleDelete = async (productId) => {
    try {
      const productRef = ref(database, `Dr/products/${productId}`);
      await remove(productRef);

      setSuccessMessage('Product deleted successfully!');
      setDeleteConfirmation(null);

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    }
  };

  const handleExport = () => {
    alert('CSV Export feature can be implemented using a library like PapaParse');
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-gray-400 ml-1" />;
    return sortConfig.direction === 'ascending'
      ? <FaSortUp className="text-teal-600 ml-1" />
      : <FaSortDown className="text-teal-600 ml-1" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'In Stock': 'bg-green-100 text-green-800',
      'Out of Stock': 'bg-red-100 text-red-800',
      'Low Stock': 'bg-yellow-100 text-yellow-800',
      'Coming Soon': 'bg-blue-100 text-blue-800',
      'Discontinued': 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles['Discontinued']}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Manage Products</h1>
          <p className="text-gray-600 mt-2">View and manage all products ({stats.total} total)</p>
          <div className="mt-6">
            <Link
              to="/admin/products/add"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:shadow-lg font-medium"
            >
              <FaPlus className="mr-2" /> Add New Product
            </Link>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
            <FaCheckCircle className="text-green-600 text-xl mr-3" />
            <p className="font-medium text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600">Total</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <FaBox className="text-3xl text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600">In Stock</p>
                <p className="text-3xl font-bold text-green-600">{stats.inStock}</p>
              </div>
              <FaCheckCircle className="text-3xl text-green-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600">Low Stock</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.lowStock}</p>
              </div>
              <FaExclamationTriangle className="text-3xl text-yellow-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600">Out of Stock</p>
                <p className="text-3xl font-bold text-red-600">{stats.outOfStock}</p>
              </div>
              <FaTimes className="text-3xl text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name, manufacturer..."
                  className="pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
              >
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-end space-x-3">
              <button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('All');
                }}
                className="px-4 py-3 border rounded-xl hover:bg-gray-50 flex items-center"
              >
                <FaRedo className="mr-2" /> Reset
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-3 border rounded-xl hover:bg-gray-50 flex items-center"
              >
                <FaDownload className="mr-2" /> Export
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Product</th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('price')}>
                    Price {getSortIcon('price')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Offer</th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('finalPrice')}>
                    Final Price {getSortIcon('finalPrice')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('stockQuantity')}>
                    Stock {getSortIcon('stockQuantity')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('status')}>
                    Status {getSortIcon('status')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentProducts.length > 0 ? (
                  currentProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-teal-50/30 transition">
                      <td className="py-5 px-6">
                        <div className="flex items-center space-x-4">
                          <img
                            src={product.imageUrl || 'https://via.placeholder.com/60'}
                            alt={product.productName}
                            className="w-14 h-14 rounded-lg object-cover border"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/60'}
                          />
                          <div>
                            <p className="font-medium text-gray-900">{product.productName}</p>
                            <p className="text-sm text-gray-500">{product.manufacturer || 'Unknown'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center">
                          <FaRupeeSign className="text-gray-500 mr-1" />
                          <span>{product.price?.toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        {product.discountValue > 0 ? (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                            {product.discountValue}{product.discountType === 'percentage' ? '%' : '₹'} OFF
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">No offer</span>
                        )}
                      </td>
                      <td className="py-5 px-6 font-bold text-emerald-600">
                        ₹{product.finalPrice?.toFixed(2)}
                      </td>
                      <td className="py-5 px-6">
                        <span className={product.stockQuantity < 10 ? 'text-red-600 font-bold' : 'text-green-600'}>
                          {product.stockQuantity} units
                        </span>
                      </td>
                      <td className="py-5 px-6">
                        {getStatusBadge(product.status)}
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmation(product)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                          <button
                            onClick={() => setViewProduct(product)}
                            className="p-2 bg-teal-100 text-teal-600 rounded-lg hover:bg-teal-200"
                            title="View"
                          >
                            <FaEye />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <GiMedicinePills className="text-6xl text-gray-300 mx-auto mb-4" />
                      <p className="text-xl text-gray-500">No products found</p>
                      <p className="text-gray-400 mt-2">Add your first product to get started!</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredProducts.length > itemsPerPage && (
            <div className="p-6 border-t flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredProducts.length)} of {filteredProducts.length}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border rounded-lg disabled:opacity-50"
                >
                  <FaChevronLeft />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 rounded-lg ${currentPage === page ? 'bg-teal-600 text-white' : 'border hover:bg-gray-100'}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border rounded-lg disabled:opacity-50"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Details Modal */}
      {viewProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <FaEye className="mr-3" />
                Product Details
              </h2>
              <button
                onClick={() => setViewProduct(null)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Product Image and Basic Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-4">
                  <img
                    src={viewProduct.imageUrl || 'https://via.placeholder.com/400'}
                    alt={viewProduct.productName}
                    className="w-full h-80 object-cover rounded-xl border-2 border-gray-200 shadow-lg"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/400'}
                  />
                  <div className="flex justify-center">
                    {getStatusBadge(viewProduct.status)}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-3xl font-bold text-gray-800 mb-2">
                      {viewProduct.productName}
                    </h3>
                    <p className="text-lg text-gray-600 flex items-center">
                      <GiMedicinePills className="mr-2 text-teal-600" />
                      {viewProduct.manufacturer || 'Unknown Manufacturer'}
                    </p>
                  </div>

                  {/* Pricing Section */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Pricing Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Original Price:</span>
                        <span className="text-lg font-semibold text-gray-800 flex items-center">
                          <FaRupeeSign className="text-sm mr-1" />
                          {viewProduct.price?.toFixed(2)}
                        </span>
                      </div>
                      {viewProduct.discountValue > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Discount:</span>
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {viewProduct.discountValue}{viewProduct.discountType === 'percentage' ? '%' : '₹'} OFF
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-emerald-300">
                        <span className="text-gray-700 font-semibold">Final Price:</span>
                        <span className="text-2xl font-bold text-emerald-600 flex items-center">
                          <FaRupeeSign className="text-lg mr-1" />
                          {viewProduct.finalPrice?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Inventory Section */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Inventory Info</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Stock Quantity:</span>
                        <span className={`text-lg font-bold ${viewProduct.stockQuantity < 10 ? 'text-red-600' : 'text-green-600'}`}>
                          {viewProduct.stockQuantity} units
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status:</span>
                        <span>{getStatusBadge(viewProduct.status)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Product Information */}
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaBox className="mr-2 text-teal-600" />
                    Product Information
                  </h4>
                  <div className="space-y-3">
                    {viewProduct.category && (
                      <div>
                        <span className="text-sm text-gray-500 block">Category</span>
                        <span className="text-gray-800 font-medium">{viewProduct.category}</span>
                      </div>
                    )}
                    {viewProduct.dosageForm && (
                      <div>
                        <span className="text-sm text-gray-500 block">Dosage Form</span>
                        <span className="text-gray-800 font-medium">{viewProduct.dosageForm}</span>
                      </div>
                    )}
                    {viewProduct.strength && (
                      <div>
                        <span className="text-sm text-gray-500 block">Strength</span>
                        <span className="text-gray-800 font-medium">{viewProduct.strength}</span>
                      </div>
                    )}
                    {viewProduct.packSize && (
                      <div>
                        <span className="text-sm text-gray-500 block">Pack Size</span>
                        <span className="text-gray-800 font-medium">{viewProduct.packSize}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaChartLine className="mr-2 text-teal-600" />
                    Additional Details
                  </h4>
                  <div className="space-y-3">
                    {viewProduct.expiryDate && (
                      <div>
                        <span className="text-sm text-gray-500 block">Expiry Date</span>
                        <span className="text-gray-800 font-medium">{viewProduct.expiryDate}</span>
                      </div>
                    )}
                    {viewProduct.batchNumber && (
                      <div>
                        <span className="text-sm text-gray-500 block">Batch Number</span>
                        <span className="text-gray-800 font-medium">{viewProduct.batchNumber}</span>
                      </div>
                    )}
                    {viewProduct.prescriptionRequired !== undefined && (
                      <div>
                        <span className="text-sm text-gray-500 block">Prescription Required</span>
                        <span className={`font-medium ${viewProduct.prescriptionRequired ? 'text-red-600' : 'text-green-600'}`}>
                          {viewProduct.prescriptionRequired ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    {viewProduct.id && (
                      <div>
                        <span className="text-sm text-gray-500 block">Product ID</span>
                        <span className="text-gray-800 font-mono text-sm">{viewProduct.id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {viewProduct.description && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <FaBox className="mr-2 text-purple-600" />
                    Description
                  </h4>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {viewProduct.description}
                  </p>
                </div>
              )}

              {/* Usage Instructions */}
              {viewProduct.usageInstructions && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200 mt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <FaExclamationTriangle className="mr-2 text-amber-600" />
                    Usage Instructions
                  </h4>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {viewProduct.usageInstructions}
                  </p>
                </div>
              )}

              {/* Side Effects */}
              {viewProduct.sideEffects && (
                <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-xl border border-red-200 mt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <FaExclamationTriangle className="mr-2 text-red-600" />
                    Side Effects
                  </h4>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {viewProduct.sideEffects}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end space-x-3 border-t">
              <button
                onClick={() => {
                  setViewProduct(null);
                  navigate(`/admin/products/edit/${viewProduct.id}`);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium flex items-center transition"
              >
                <FaEdit className="mr-2" />
                Edit Product
              </button>
              <button
                onClick={() => setViewProduct(null)}
                className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-100 font-medium transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaExclamationTriangle className="text-4xl text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Delete Product?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{deleteConfirmation.productName}</strong>?<br />
                This action cannot be undone.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirmation.id)}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminViewProducts;